import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { useAuth } from '../../context/AuthContext';
import { mergePrefill } from '../../types/onboardingIntake';
import { api, apiEndpoints } from '../../utils/apiService';
import OnboardingIntakePrefillControl from './OnboardingIntakePrefillControl';

type TfvFields = {
  customer_profile_sid: string;
  business_name: string;
  business_website: string;
  notification_email: string;
  use_case_categories: string[];
  use_case_summary: string;
  production_message_sample: string;
  opt_in_image_urls: string[];
  opt_in_type: string;
  message_volume: string;
  tollfree_phone_number_sid: string;
  business_street_address: string;
  business_street_address2: string;
  business_city: string;
  business_state_province_region: string;
  business_postal_code: string;
  business_country: string;
  business_contact_first_name: string;
  business_contact_last_name: string;
  business_contact_email: string;
  business_contact_phone: string;
  additional_information: string;
  business_registration_number: string;
  business_registration_authority: string;
  business_registration_country: string;
  business_type: string;
  business_registration_phone_number: string;
  doing_business_as: string;
  opt_in_confirmation_message: string;
  help_message_sample: string;
  privacy_policy_url: string;
  terms_and_conditions_url: string;
  age_gated_content: boolean;
  opt_in_keywords: string[];
  vetting_provider: string;
  vetting_id: string;
};

type TfvRequest = {
  id?: number;
  sid?: string;
  status?: string;
  tollfree_phone_number_sid?: string;
  business_name?: string;
  [key: string]: unknown;
};

type TfvPrefillResponse = {
  store: number;
  fields: Partial<TfvFields>;
  latest_request: TfvRequest | null;
};

type AvailablePhoneNumber = {
  sid: string;
  phone_number: string;
  friendly_name?: string;
  capabilities?: {
    sms?: boolean;
    voice?: boolean;
  };
};

type SecondaryCustomerProfile = {
  sid?: string;
  profile_sid?: string;
  friendly_name?: string;
  business_name?: string;
  customer_name?: string;
  status?: string;
  [key: string]: unknown;
};

type TextFieldConfig = {
  name: keyof TfvFields;
  label: string;
  type?: string;
  required?: boolean;
  textarea?: boolean;
};

interface TwilioTfvVerificationTabProps {
  storeOptions: SearchableSelectOption[];
}

const TFV_PRIVACY_POLICY_URL = 'https://dashboard.askarthur.io/legal/privacy';
const TFV_TERMS_URL = 'https://dashboard.askarthur.io/legal/terms';

const withHardcodedLegalUrls = (fields: TfvFields): TfvFields => ({
  ...fields,
  privacy_policy_url: TFV_PRIVACY_POLICY_URL,
  terms_and_conditions_url: TFV_TERMS_URL,
});

const emptyTfvFields: TfvFields = {
  customer_profile_sid: '',
  business_name: '',
  business_website: '',
  notification_email: '',
  use_case_categories: ['CUSTOMER_CARE'],
  use_case_summary: '',
  production_message_sample: '',
  opt_in_image_urls: [],
  opt_in_type: 'WEB_FORM',
  message_volume: '1,000',
  tollfree_phone_number_sid: '',
  business_street_address: '',
  business_street_address2: '',
  business_city: '',
  business_state_province_region: '',
  business_postal_code: '',
  business_country: '',
  business_contact_first_name: '',
  business_contact_last_name: '',
  business_contact_email: '',
  business_contact_phone: '',
  additional_information: '',
  business_registration_number: '',
  business_registration_authority: 'EIN',
  business_registration_country: 'US',
  business_type: 'PRIVATE_PROFIT',
  business_registration_phone_number: '',
  doing_business_as: '',
  opt_in_confirmation_message: '',
  help_message_sample: '',
  privacy_policy_url: TFV_PRIVACY_POLICY_URL,
  terms_and_conditions_url: TFV_TERMS_URL,
  age_gated_content: false,
  opt_in_keywords: [],
  vetting_provider: '',
  vetting_id: '',
};

const useCaseCategoryOptions = [
  'CUSTOMER_CARE',
  'DELIVERY_NOTIFICATIONS',
  'ACCOUNT_NOTIFICATIONS',
  'EVENTS',
  'MARKETING',
];

const selectOptionsByField: Partial<Record<keyof TfvFields, string[]>> = {
  opt_in_type: [
    'VERBAL',
    'WEB_FORM',
    'PAPER_FORM',
    'VIA_TEXT',
    'MOBILE_QR_CODE',
    'IMPORT',
  ],
  business_type: [
    'PRIVATE_PROFIT',
    'PUBLIC_PROFIT',
    'SOLE_PROPRIETOR',
    'NON_PROFIT',
    'GOVERNMENT',
  ],
  business_registration_authority: [
    'EIN',
    'CBN',
    'CRN',
    'PROVINCIAL_NUMBER',
    'VAT',
    'ACN',
    'ABN',
    'BRN',
    'SIREN',
    'SIRET',
  ],
  business_registration_country: ['US', 'CA'],
  vetting_provider: ['', 'CAMPAIGN_VERIFY'],
};

const primaryFields: TextFieldConfig[] = [
  { name: 'business_name', label: 'Business Name' },
  { name: 'business_website', label: 'Business Website' },
  { name: 'notification_email', label: 'Notification Email', type: 'email' },
  { name: 'message_volume', label: 'Message Volume' },
];

const addressFields: TextFieldConfig[] = [
  { name: 'business_street_address', label: 'Street Address' },
  { name: 'business_street_address2', label: 'Street Address 2' },
  { name: 'business_city', label: 'City' },
  {
    name: 'business_state_province_region',
    label: 'State / Province / Region',
  },
  { name: 'business_postal_code', label: 'Postal Code' },
  { name: 'business_country', label: 'Country' },
];

const contactFields: TextFieldConfig[] = [
  { name: 'business_contact_first_name', label: 'Contact First Name' },
  { name: 'business_contact_last_name', label: 'Contact Last Name' },
  { name: 'business_contact_email', label: 'Contact Email', type: 'email' },
  { name: 'business_contact_phone', label: 'Contact Phone' },
];

const registrationFields: TextFieldConfig[] = [
  { name: 'business_registration_number', label: 'Registration Number' },
  { name: 'business_registration_authority', label: 'Registration Authority' },
  { name: 'business_registration_country', label: 'Registration Country' },
  { name: 'business_type', label: 'Business Type' },
  {
    name: 'business_registration_phone_number',
    label: 'Registration Phone Number',
  },
  { name: 'doing_business_as', label: 'Doing Business As' },
];

const messagingFields: TextFieldConfig[] = [
  { name: 'use_case_summary', label: 'Use Case Summary', textarea: true },
  {
    name: 'production_message_sample',
    label: 'Production Message Sample',
    textarea: true,
  },
  { name: 'opt_in_type', label: 'Opt-In Type' },
  {
    name: 'opt_in_confirmation_message',
    label: 'Opt-In Confirmation Message',
    textarea: true,
  },
  { name: 'help_message_sample', label: 'Help Message Sample', textarea: true },
  { name: 'privacy_policy_url', label: 'Privacy Policy URL' },
  { name: 'terms_and_conditions_url', label: 'Terms and Conditions URL' },
  {
    name: 'additional_information',
    label: 'Additional Information',
    textarea: true,
  },
  { name: 'vetting_provider', label: 'Vetting Provider' },
  { name: 'vetting_id', label: 'Vetting ID' },
];

const readSelectedStoreId = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('currentStoreId') || '';
};

const splitLines = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const joinLines = (value: string[]) => value.join('\n');

const getResponseError = async (response: Response, fallback: string) => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.detail || data.message || data.error || fallback;
  }
  return fallback;
};

const statusClassName = (status?: string) => {
  if (status === 'TWILIO_APPROVED' || status === 'APPROVED') {
    return 'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/20 dark:text-success-400 dark:ring-success-800';
  }
  if (status === 'TWILIO_REJECTED' || status === 'REJECTED') {
    return 'bg-error-50 text-error-700 ring-error-200 dark:bg-error-900/20 dark:text-error-400 dark:ring-error-800';
  }
  if (status === 'PENDING_REVIEW' || status === 'IN_REVIEW') {
    return 'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-900/20 dark:text-warning-400 dark:ring-warning-800';
  }
  return 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
};

const getSecondaryProfileSid = (profile: SecondaryCustomerProfile) =>
  profile.profile_sid || profile.sid || '';

/**
 * EIN is the US default registration authority and CBN the Canadian one;
 * they travel as a pair with the registration country. Reconciling only
 * ever moves the authority between those two defaults — an explicit
 * choice such as PROVINCIAL_NUMBER or VAT is left alone.
 */
const defaultRegistrationAuthority = (country?: string) =>
  country === 'CA' ? 'CBN' : 'EIN';

const reconcileRegistrationAuthority = (fields: TfvFields): TfvFields => {
  const authority = fields.business_registration_authority;
  return !authority || authority === 'EIN' || authority === 'CBN'
    ? {
        ...fields,
        business_registration_authority: defaultRegistrationAuthority(
          fields.business_registration_country,
        ),
      }
    : fields;
};

// Reconciled, not just defaulted: a request saved before CBN became the
// Canadian default carries an explicit EIN next to country CA, and the
// pairing has to correct that on load too.
const normalizeFields = (fields?: Partial<TfvFields>): TfvFields =>
  reconcileRegistrationAuthority(
    withHardcodedLegalUrls({
      ...emptyTfvFields,
      ...(fields || {}),
      use_case_categories: fields?.use_case_categories?.length
        ? fields.use_case_categories
        : ['CUSTOMER_CARE'],
      opt_in_image_urls: fields?.opt_in_image_urls || [],
      opt_in_type: fields?.opt_in_type || 'WEB_FORM',
      business_registration_country:
        fields?.business_registration_country || 'US',
      business_type: fields?.business_type || 'PRIVATE_PROFIT',
      opt_in_keywords: fields?.opt_in_keywords || [],
      age_gated_content: Boolean(fields?.age_gated_content),
    }),
  );

const TwilioTfvVerificationTab: React.FC<TwilioTfvVerificationTabProps> = ({
  storeOptions,
}) => {
  const { user } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState(readSelectedStoreId);
  const [fields, setFields] = useState<TfvFields>(emptyTfvFields);
  const [latestRequest, setLatestRequest] = useState<TfvRequest | null>(null);
  const [submittedRequest, setSubmittedRequest] = useState<TfvRequest | null>(
    null,
  );
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState<
    AvailablePhoneNumber[]
  >([]);
  const [secondaryCustomerProfiles, setSecondaryCustomerProfiles] = useState<
    SecondaryCustomerProfile[]
  >([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [loadingCustomerProfiles, setLoadingCustomerProfiles] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedStoreLabel = useMemo(
    () =>
      storeOptions.find((option) => String(option.value) === selectedStoreId)
        ?.label || (selectedStoreId ? `Store ID ${selectedStoreId}` : ''),
    [selectedStoreId, storeOptions],
  );

  const availablePhoneNumberOptions = useMemo(() => {
    const options = availablePhoneNumbers.map((number) => ({
      value: number.sid,
      label: `${number.phone_number} (${number.sid})${
        number.friendly_name ? ` - ${number.friendly_name}` : ''
      }`,
    }));

    if (
      fields.tollfree_phone_number_sid &&
      !options.some(
        (option) => option.value === fields.tollfree_phone_number_sid,
      )
    ) {
      options.unshift({
        value: fields.tollfree_phone_number_sid,
        label: `Prefilled SID (${fields.tollfree_phone_number_sid})`,
      });
    }

    return options;
  }, [availablePhoneNumbers, fields.tollfree_phone_number_sid]);

  const selectedPhoneNumber = useMemo(
    () =>
      availablePhoneNumbers.find(
        (number) => number.sid === fields.tollfree_phone_number_sid,
      ),
    [availablePhoneNumbers, fields.tollfree_phone_number_sid],
  );

  const secondaryCustomerProfileOptions = useMemo(() => {
    const options = secondaryCustomerProfiles.reduce<SearchableSelectOption[]>(
      (acc, profile) => {
        const sid = getSecondaryProfileSid(profile);
        if (!sid) return acc;

        const name =
          profile.friendly_name ||
          profile.business_name ||
          profile.customer_name ||
          'Secondary Customer Profile';

        acc.push({
          value: sid,
          label: `${name} (${sid})${
            profile.status ? ` - ${profile.status}` : ''
          }`,
        });
        return acc;
      },
      [],
    );

    if (
      fields.customer_profile_sid &&
      !options.some((option) => option.value === fields.customer_profile_sid)
    ) {
      options.unshift({
        value: fields.customer_profile_sid,
        label: `Prefilled SID (${fields.customer_profile_sid})`,
      });
    }

    return options;
  }, [fields.customer_profile_sid, secondaryCustomerProfiles]);

  const selectedCustomerProfile = useMemo(
    () =>
      secondaryCustomerProfiles.find(
        (profile) =>
          getSecondaryProfileSid(profile) === fields.customer_profile_sid,
      ),
    [fields.customer_profile_sid, secondaryCustomerProfiles],
  );

  const fetchAvailablePhoneNumbers = useCallback(async () => {
    if (!user?.is_platform_admin) return;

    setLoadingPhoneNumbers(true);
    try {
      const response = await api.get(
        apiEndpoints.getAvailableTwilioPhoneNumbers(),
      );
      if (response?.ok) {
        const data = await response.json();
        setAvailablePhoneNumbers(
          Array.isArray(data.phone_numbers) ? data.phone_numbers : [],
        );
      } else {
        setAvailablePhoneNumbers([]);
      }
    } catch (err) {
      console.error('Available Twilio phone numbers error:', err);
      setAvailablePhoneNumbers([]);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  }, [user?.is_platform_admin]);

  useEffect(() => {
    fetchAvailablePhoneNumbers();
  }, [fetchAvailablePhoneNumbers]);

  const fetchSecondaryCustomerProfiles = useCallback(async () => {
    if (!user?.is_platform_admin) return;

    setLoadingCustomerProfiles(true);
    try {
      const response = await api.get(
        apiEndpoints.getTwilioSecondaryCustomerProfiles(),
      );
      if (response?.ok) {
        const data = await response.json();
        const profiles = Array.isArray(data)
          ? data
          : Array.isArray(data.profiles)
            ? data.profiles
            : Array.isArray(data.customer_profiles)
              ? data.customer_profiles
              : Array.isArray(data.results)
                ? data.results
                : [];
        setSecondaryCustomerProfiles(profiles);
      } else {
        setSecondaryCustomerProfiles([]);
      }
    } catch (err) {
      console.error('Secondary customer profiles error:', err);
      setSecondaryCustomerProfiles([]);
    } finally {
      setLoadingCustomerProfiles(false);
    }
  }, [user?.is_platform_admin]);

  useEffect(() => {
    fetchSecondaryCustomerProfiles();
  }, [fetchSecondaryCustomerProfiles]);

  const fetchPrefill = useCallback(async () => {
    if (!user?.is_platform_admin || !selectedStoreId) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setSubmittedRequest(null);

    try {
      const response = await api.get(apiEndpoints.getTfvPrefill());
      if (response?.ok) {
        const data: TfvPrefillResponse = await response.json();
        setFields(normalizeFields(data.fields));
        setLatestRequest(data.latest_request);
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to load TFV prefill (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to load TFV prefill: No response from server');
      }
    } catch (err) {
      console.error('TFV prefill error:', err);
      setError('An unexpected error occurred while loading TFV prefill.');
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId, user?.is_platform_admin]);

  useEffect(() => {
    fetchPrefill();
  }, [fetchPrefill]);

  const handleStoreChange = (value: string) => {
    setSelectedStoreId(value);
    setFields(emptyTfvFields);
    setLatestRequest(null);
    setSubmittedRequest(null);
    setError('');
    setSuccess('');

    if (typeof window === 'undefined') return;
    if (value) {
      localStorage.setItem('currentStoreId', value);
    } else {
      localStorage.removeItem('currentStoreId');
    }
  };

  const setField = <K extends keyof TfvFields>(
    name: K,
    value: TfvFields[K],
  ) => {
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneNumberChange = (sid: string) => {
    setField('tollfree_phone_number_sid', sid);
  };

  const handleCustomerProfileChange = (sid: string) => {
    setField('customer_profile_sid', sid);
  };

  const handleTextInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setField(name as keyof TfvFields, value as never);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'business_registration_country') {
      // Keep the default authority in step with the country (US → EIN,
      // CA → CBN) without touching an explicit non-default choice.
      setFields((prev) =>
        reconcileRegistrationAuthority({
          ...prev,
          business_registration_country: value,
        }),
      );
      return;
    }
    setField(name as keyof TfvFields, value as never);
  };

  const toggleUseCaseCategory = (category: string) => {
    setFields((prev) => {
      const nextCategories = prev.use_case_categories.includes(category)
        ? prev.use_case_categories.filter((value) => value !== category)
        : [...prev.use_case_categories, category];

      return {
        ...prev,
        use_case_categories: nextCategories.length
          ? nextCategories
          : ['CUSTOMER_CARE'],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldsBeforeSubmit = fields;
    setError('');
    setSuccess('');
    setSubmittedRequest(null);

    const phoneSid = fields.tollfree_phone_number_sid.trim();
    if (!phoneSid) {
      setError('Select an available toll-free phone number before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const authority =
        fields.business_registration_authority ||
        defaultRegistrationAuthority(fields.business_registration_country);
      const submitFields = withHardcodedLegalUrls({
        ...fields,
        business_registration_authority: authority,
        business_registration_country:
          fields.business_registration_country || 'US',
        // An EIN or CBN is digits (a CBN may carry a program account);
        // Twilio rejects the pasted-in hyphens and spaces.
        business_registration_number:
          authority === 'EIN' || authority === 'CBN'
            ? fields.business_registration_number.replace(/[\s.-]/g, '')
            : fields.business_registration_number,
        business_type: fields.business_type || 'PRIVATE_PROFIT',
      });

      const response = await api.post(apiEndpoints.submitTfv(), {
        phone_sid: phoneSid,
        fields: submitFields,
      });

      if (response?.ok) {
        const data = await response.json();
        setSubmittedRequest(data.request || null);
        setLatestRequest(data.request || latestRequest);
        setSuccess(
          data.sid
            ? `TFV submitted successfully. SID: ${data.sid}`
            : data.message || 'TFV submitted successfully.',
        );
        await fetchAvailablePhoneNumbers();
      } else if (response) {
        setFields(fieldsBeforeSubmit);
        setError(
          await getResponseError(
            response,
            `Failed to submit TFV (Status: ${response.status})`,
          ),
        );
      } else {
        setFields(fieldsBeforeSubmit);
        setError('Failed to submit TFV: No response from server');
      }
    } catch (err) {
      console.error('TFV submit error:', err);
      setFields(fieldsBeforeSubmit);
      setError('An unexpected error occurred while submitting TFV.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIntakePrefill = (
    prefill: Record<string, any>,
    overwrite: boolean,
  ) => {
    const safePrefill = { ...prefill };
    delete safePrefill.tollfree_phone_number_sid;
    delete safePrefill.additional_information;
    setFields((prev) =>
      reconcileRegistrationAuthority(
        withHardcodedLegalUrls(
          mergePrefill(prev as any, safePrefill as any, overwrite),
        ),
      ),
    );
    setSuccess('TFV fields populated from onboarding intake.');
  };

  const renderTextField = (field: TextFieldConfig) => {
    const selectOptions = selectOptionsByField[field.name];

    if (selectOptions) {
      return (
        <div key={field.name}>
          <Label>{field.label}</Label>
          <select
            name={field.name}
            value={String(fields[field.name] || '')}
            onChange={handleSelect}
            disabled={loading || submitting}
            className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
          >
            {selectOptions.map((option) => (
              <option key={option || 'none'} value={option}>
                {option || 'None'}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.textarea) {
      return (
        <div key={field.name} className="md:col-span-2">
          <Label>{field.label}</Label>
          <textarea
            name={field.name}
            value={String(fields[field.name] || '')}
            onChange={handleTextInput}
            disabled={loading || submitting}
            rows={4}
            className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
          />
        </div>
      );
    }

    return (
      <div key={field.name}>
        <Label>
          {field.label}
          {field.required ? <span className="text-error-500"> *</span> : null}
        </Label>
        <Input
          type={field.type || 'text'}
          name={field.name}
          value={String(fields[field.name] || '')}
          onChange={handleTextInput}
          required={field.required}
          disabled={
            loading ||
            submitting ||
            field.name === 'privacy_policy_url' ||
            field.name === 'terms_and_conditions_url'
          }
        />
      </div>
    );
  };

  if (!user?.is_platform_admin) {
    return (
      <ComponentCard title="TFV Verification" className="overflow-hidden">
        <div className="p-4 text-sm text-error-500">
          This tool is only available to platform admins.
        </div>
      </ComponentCard>
    );
  }

  const visibleRequest = submittedRequest || latestRequest;
  const canSubmit =
    !!selectedStoreId &&
    !!fields.tollfree_phone_number_sid.trim() &&
    !submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <ComponentCard title="TFV Store" className="overflow-hidden">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label>Selected Store</Label>
            <SearchableSelect
              options={storeOptions}
              value={selectedStoreId}
              onChange={handleStoreChange}
              placeholder="Select a store"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Requests use the selected store as the X-Store-ID header.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchPrefill}
            disabled={!selectedStoreId || loading || submitting}
            className="inline-flex items-center justify-center rounded-lg bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {loading ? 'Loading...' : 'Reload Prefill'}
          </button>
        </div>
      </ComponentCard>

      {!selectedStoreId ? (
        <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
          Select a store before loading or submitting TFV.
        </div>
      ) : null}

      <OnboardingIntakePrefillControl
        target="tfv"
        disabled={!selectedStoreId || loading || submitting}
        onApply={handleIntakePrefill}
      />

      <ComponentCard title="Latest TFV Request" className="overflow-hidden">
        {visibleRequest ? (
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Store
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                {selectedStoreLabel}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Status
              </p>
              <span
                className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusClassName(
                  visibleRequest.status,
                )}`}
              >
                {visibleRequest.status || 'Unknown'}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Request SID
              </p>
              <p className="mt-1 break-all text-sm font-semibold text-gray-800 dark:text-white/90">
                {visibleRequest.sid || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Phone SID
              </p>
              <p className="mt-1 break-all text-sm font-semibold text-gray-800 dark:text-white/90">
                {visibleRequest.tollfree_phone_number_sid || 'N/A'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No TFV request found for this store.
          </p>
        )}
      </ComponentCard>

      <ComponentCard title="TFV Details" className="overflow-hidden">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <div className="md:col-span-2 xl:col-span-3">
            <Label>
              Toll-Free Phone Number <span className="text-error-500">*</span>
            </Label>
            <SearchableSelect
              options={availablePhoneNumberOptions}
              value={fields.tollfree_phone_number_sid}
              onChange={handlePhoneNumberChange}
              placeholder={
                loadingPhoneNumbers
                  ? 'Loading available numbers...'
                  : 'Select an available number'
              }
              disabled={loading || submitting || loadingPhoneNumbers}
              required
            />
            {selectedPhoneNumber ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Selected SID {selectedPhoneNumber.sid}. Capabilities:{' '}
                {selectedPhoneNumber.capabilities?.sms ? 'SMS' : 'No SMS'}
                {', '}
                {selectedPhoneNumber.capabilities?.voice ? 'Voice' : 'No Voice'}
                .
              </p>
            ) : fields.tollfree_phone_number_sid ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Using prefilled SID {fields.tollfree_phone_number_sid}.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Choose one of the available Twilio phone numbers for this TFV
                submission.
              </p>
            )}
          </div>
          <div className="md:col-span-2 xl:col-span-3">
            <Label>Customer Profile</Label>
            <SearchableSelect
              options={secondaryCustomerProfileOptions}
              value={fields.customer_profile_sid}
              onChange={handleCustomerProfileChange}
              placeholder={
                loadingCustomerProfiles
                  ? 'Loading customer profiles...'
                  : 'Select a secondary customer profile'
              }
              disabled={loading || submitting || loadingCustomerProfiles}
            />
            {selectedCustomerProfile ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Selected SID {getSecondaryProfileSid(selectedCustomerProfile)}
                {selectedCustomerProfile.status
                  ? `. Status: ${selectedCustomerProfile.status}.`
                  : '.'}
              </p>
            ) : fields.customer_profile_sid ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Using prefilled customer profile SID{' '}
                {fields.customer_profile_sid}.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Choose a Twilio secondary customer profile to associate with the
                TFV submission.
              </p>
            )}
          </div>
          {primaryFields.map(renderTextField)}
        </div>
      </ComponentCard>

      <ComponentCard title="Business Address" className="overflow-hidden">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {addressFields.map(renderTextField)}
        </div>
      </ComponentCard>

      <ComponentCard title="Business Contact" className="overflow-hidden">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {contactFields.map(renderTextField)}
        </div>
      </ComponentCard>

      <ComponentCard title="Business Registration" className="overflow-hidden">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {registrationFields.map(renderTextField)}
        </div>
      </ComponentCard>

      <ComponentCard title="Messaging and Opt-In" className="overflow-hidden">
        <div className="mb-5">
          <Label>Use Case Categories</Label>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {useCaseCategoryOptions.map((category) => {
              const checked = fields.use_case_categories.includes(category);
              return (
                <label
                  key={category}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                    checked
                      ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-brand-200 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-brand-500/30'
                  } ${loading || submitting ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUseCaseCategory(category)}
                    disabled={loading || submitting}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span>{category}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {messagingFields.map(renderTextField)}
          <div className="md:col-span-2">
            <Label>Opt-In Image URLs</Label>
            <textarea
              value={joinLines(fields.opt_in_image_urls)}
              onChange={(e) =>
                setField('opt_in_image_urls', splitLines(e.target.value))
              }
              disabled={loading || submitting}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              One URL per line.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>Opt-In Keywords</Label>
            <textarea
              value={joinLines(fields.opt_in_keywords)}
              onChange={(e) =>
                setField('opt_in_keywords', splitLines(e.target.value))
              }
              disabled={loading || submitting}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              One keyword per line.
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
            <input
              type="checkbox"
              checked={fields.age_gated_content}
              onChange={(e) => setField('age_gated_content', e.target.checked)}
              disabled={loading || submitting}
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            Age gated content
          </label>
        </div>
      </ComponentCard>

      {error ? (
        <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400">
          {success}
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!canSubmit}
        className="inline-flex w-full items-center justify-center rounded-lg bg-brand-500 px-5 py-3.5 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
      >
        {submitting ? 'Submitting TFV...' : 'Submit TFV'}
      </button>
    </form>
  );
};

export default TwilioTfvVerificationTab;
